/**
 * Headless selectors + formatters for the Insights → Daily tab.
 *
 * Platform-neutral (no DOM, no recharts) per the shared-core contract. The
 * web-shell chart component consumes these to project the two revenue lenses at
 * daily grain plus the quoting overlay, and to render KPI/tooltip figures
 * consistently. The monthly-tab sibling is ``insights-monthly-revenue.ts``;
 * this one adds day-specific label formatting (short date + weekday), weekend
 * detection, and month-boundary detection for the divider lines.
 *
 * Vocabulary (locked): "quoted value" = deduped one row per enquiry; "gross
 * quoted" = raw pre-dedupe sum (tooltip only on this tab); "won value" =
 * quoted filtered to won.
 */

import type { InsightsDailyRevenuePoint } from "../data/api-types";
import { formatGbpCompact, niceAxisMax } from "./insights-monthly-revenue";
import { LONG_MONTHS, SHORT_MONTHS } from "./formatters";

export { formatGbpCompact, niceAxisMax };

export type RevenueBasis = "booked" | "invoiced";

/** One day projected to the selected basis — the shape the chart plots. */
export interface DailyRevenueDatum {
  day: string;
  /** Short axis/tooltip label, e.g. "7 Jul". */
  label: string;
  /** Weekday short name, e.g. "Tue". */
  weekday: string;
  /** True for Saturday/Sunday — the near-zero bars. */
  isWeekend: boolean;
  revenue: number;
  contribution: number;
  /** contribution / revenue as a percentage (0 when revenue is 0). */
  marginPct: number;
  /** Deduped one-row-per-enquiry estimate value (the amber line). */
  quotedValue: number;
  /** Raw pre-dedupe sum over every revision (tooltip only). */
  grossQuoted: number;
  /** Quoted value filtered to won. */
  wonValue: number;
  /** True when this day is the first of its month (a divider anchor). */
  isMonthStart: boolean;
  /** Full month name when isMonthStart, else "" — the divider label. */
  monthLabel: string;
}

const _WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** ISO day ("YYYY-MM-DD") → "7 Jul" style short label. */
export function shortDayLabel(day: string): string {
  const [y, m, d] = day.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return day;
  // Fixed month table, not Intl: avoids ICU rendering en-GB's short
  // September as "Sept" while every other label in the app says "Sep".
  return `${d} ${SHORT_MONTHS[m - 1]}`;
}

/** ISO day → [weekdayIndex 0..6 (Sun=0), dayOfMonth, fullMonthName]. */
function dayParts(day: string): {
  weekday: number;
  dayOfMonth: number;
  monthName: string;
} {
  const [y, m, d] = day.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return { weekday: 0, dayOfMonth: 0, monthName: "" };
  // Date.UTC + getUTCDay is safe for the weekday: both ends are UTC, so
  // this is pure calendar arithmetic, never a timezone conversion.
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return {
    weekday,
    dayOfMonth: d,
    monthName: LONG_MONTHS[m - 1] ?? "",
  };
}

/** Project each day to the chosen revenue basis (booked or invoiced). */
export function projectDailyRevenue(
  days: InsightsDailyRevenuePoint[],
  basis: RevenueBasis,
): DailyRevenueDatum[] {
  return days.map((p) => {
    const revenue = basis === "booked" ? p.booked_revenue : p.invoiced_revenue;
    const contribution =
      basis === "booked" ? p.booked_contribution : p.invoiced_contribution;
    const { weekday, dayOfMonth, monthName } = dayParts(p.day);
    const isMonthStart = dayOfMonth === 1;
    return {
      day: p.day,
      label: shortDayLabel(p.day),
      weekday: _WEEKDAYS[weekday],
      isWeekend: weekday === 0 || weekday === 6,
      revenue,
      contribution,
      marginPct: revenue ? (100 * contribution) / revenue : 0,
      quotedValue: p.quoted_value ?? 0,
      grossQuoted: p.gross_quoted ?? 0,
      wonValue: p.won_value ?? 0,
      isMonthStart,
      monthLabel: isMonthStart ? monthName : "",
    };
  });
}

/** Total revenue over the window for the selected basis (KPI). */
export function totalRevenue(data: DailyRevenueDatum[]): number {
  return data.reduce((s, d) => s + d.revenue, 0);
}

/** Blended contribution margin % over the window (0 when no revenue). */
export function blendedMarginPct(data: DailyRevenueDatum[]): number {
  const rev = totalRevenue(data);
  const con = data.reduce((s, d) => s + d.contribution, 0);
  return rev ? (100 * con) / rev : 0;
}

/** Total quoted value over the window (KPI). */
export function totalQuotedValue(data: DailyRevenueDatum[]): number {
  return data.reduce((s, d) => s + d.quotedValue, 0);
}

/** Quoted-value ÷ revenue multiple over the window (0 when no revenue). */
export function quotedRevenueMultiple(data: DailyRevenueDatum[]): number {
  const rev = totalRevenue(data);
  return rev ? totalQuotedValue(data) / rev : 0;
}

/** The peak revenue day in the window (null when empty). */
export function peakDay(data: DailyRevenueDatum[]): DailyRevenueDatum | null {
  if (data.length === 0) return null;
  return data.reduce(
    (best, d) => (d.revenue > best.revenue ? d : best),
    data[0],
  );
}
